import React, { useEffect, useRef, useState } from "react";

/**
 * PlanTransitionOverlay — Phase C Feature 12.
 *
 * When the user opens a unit that has `plan_transition_video_url` set,
 * we play that cinematic video full-screen before revealing the plan.
 * Skip on tap. Auto-completes when the video ends or fails to load
 * within a short window.
 */
export default function PlanTransitionOverlay({ url, onComplete }) {
  const ref = useRef(null);
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!url) { onComplete?.(); return; }
    const v = ref.current;
    if (!v) return;
    let safety = setTimeout(() => { onComplete?.(); }, 8000); // hard cap

    const onLoaded = () => setArmed(true);
    const onEnd = () => { clearTimeout(safety); onComplete?.(); };
    const onErr = () => { clearTimeout(safety); onComplete?.(); };

    v.addEventListener("loadeddata", onLoaded);
    v.addEventListener("ended", onEnd);
    v.addEventListener("error", onErr);

    // Best-effort play; muted so iOS allows it without a user gesture.
    v.play().catch(() => { /* ignore — user can tap to skip */ });

    return () => {
      clearTimeout(safety);
      v.removeEventListener("loadeddata", onLoaded);
      v.removeEventListener("ended", onEnd);
      v.removeEventListener("error", onErr);
    };
  }, [url, onComplete]);

  if (!url) return null;

  return (
    <div
      className="absolute inset-0 z-[60] bg-black flex items-center justify-center fade-in cursor-pointer"
      onClick={() => onComplete?.()}
      data-testid="plan-transition-overlay"
    >
      <video
        ref={ref}
        src={url}
        muted
        autoPlay
        playsInline
        className="w-full h-full object-cover"
        data-testid="plan-transition-video"
      />
      <div className="absolute bottom-6 right-6 text-[10px] uppercase tracking-[0.3em] text-white/70 pointer-events-none">
        Tap to skip
      </div>
      {!armed && (
        <div className="absolute inset-0 bg-black/40" />
      )}
    </div>
  );
}
